// src/pages/admin/AdminApprovals.jsx
// ============================================================
// Admin approvals control panel.
// Displays pending profile updates requested by Employees.
// Provides side-by-side comparison of old vs new values.
// ============================================================

import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  usePendingApprovals,
  approveRequest,
  rejectRequest,
} from "../../hooks/useEmployeeMaster";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

export default function AdminApprovals() {
  const { userProfile } = useAuth();
  const { data: pendingRequests, loading } = usePendingApprovals();

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentValues, setCurrentValues] = useState({});
  const [rejectComment, setRejectComment] = useState("");
  const [processing, setProcessing] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadCurrentValues = async (req) => {
    setCurrentValues({});
    try {
      const uSnap = await getDoc(doc(db, "users", req.employeeId));
      const uData = uSnap.exists() ? uSnap.data() : {};

      const pSnap = await getDoc(doc(db, "users", req.employeeId, "personal_details", "main"));
      const pData = pSnap.exists() ? pSnap.data() : {};

      const bSnap = await getDoc(doc(db, "users", req.employeeId, "bank_details", "main"));
      const bData = bSnap.exists() ? bSnap.data() : {};

      setCurrentValues({
        ...uData,
        ...pData,
        ...bData,
        branch: bData.branch || "", // bank subcollection field
      });
    } catch (err) {
      console.error("[AdminApprovals] loadCurrentValues error:", err);
    }
  };

  useEffect(() => {
    if (selectedRequest) {
      loadCurrentValues(selectedRequest);
    }
  }, [selectedRequest]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    setMsg("");
    setErr("");
    try {
      await approveRequest(selectedRequest.id, userProfile.uid, userProfile.displayName || "Admin");
      setMsg("✅ Profile changes approved successfully.");
      setSelectedRequest(null);
      setRejectComment("");
    } catch (e) {
      setErr(e.message || "Failed to approve request.");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!rejectComment.trim()) {
      setErr("⚠️ Rejection comments are required to reject the changes.");
      return;
    }
    setProcessing(true);
    setMsg("");
    setErr("");
    try {
      await rejectRequest(selectedRequest.id, userProfile.uid, userProfile.displayName || "Admin", rejectComment);
      setMsg("✅ Requested changes rejected.");
      setSelectedRequest(null);
      setRejectComment("");
    } catch (e) {
      setErr(e.message || "Failed to reject request.");
    } finally {
      setProcessing(false);
    }
  };

  const formatFieldName = (key) => {
    const map = {
      aadhaar: "Aadhaar Card",
      pan: "PAN Card",
      passport: "Passport",
      bankName: "Bank Name",
      bankBranch: "Bank Branch",
      accountNumber: "Account Number",
      ifscCode: "IFSC Code",
      accountHolder: "Account Holder",
    };
    return map[key] || key.replace(/([A-Z])/g, " $1").trim();
  };

  const maskValue = (key, val) => {
    if (!val) return "—";
    if (key === "aadhaar") return "XXXX XXXX " + val.slice(-4);
    if (key === "pan") return "XXXXX" + val.slice(-5).toUpperCase();
    if (key === "accountNumber") return "XXXXXX" + val.slice(-4);
    return val;
  };

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <style>{`
        .approvals-layout {
          display: grid;
          grid-template-columns: 340px 1fr;
          gap: 1.5rem;
          margin-top: 1.5rem;
        }
        .approvals-list-panel {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1rem;
          max-height: calc(100vh - 200px);
          overflow-y: auto;
        }
        .approval-item-card {
          padding: 0.875rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          margin-bottom: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
          background: var(--bg-secondary);
        }
        .approval-item-card:hover {
          border-color: var(--brand-primary, #1b4b31);
        }
        .approval-item-card--selected {
          border-color: var(--brand-primary, #1b4b31);
          background: rgba(27, 75, 49, 0.05);
          box-shadow: 0 2px 8px rgba(27, 75, 49, 0.1);
        }
        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .comparison-table th, .comparison-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border);
          text-align: left;
          font-size: 0.82rem;
        }
        .comparison-table th {
          font-weight: 600;
          color: var(--text-primary);
          background: var(--bg-secondary);
        }
        .value-old {
          color: var(--red);
          background: rgba(239, 68, 68, 0.05);
          border-radius: 4px;
          padding: 0.2rem 0.4rem;
          text-decoration: line-through;
          font-family: var(--font-mono);
        }
        .value-new {
          color: var(--green);
          background: rgba(16, 185, 129, 0.08);
          border-radius: 4px;
          padding: 0.2rem 0.4rem;
          font-weight: 500;
          font-family: var(--font-mono);
        }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Approval Requests</h1>
          <p className="page-subtitle">Verify and authorize sensitive profile changes submitted by employees</p>
        </div>
      </div>

      {msg && <div className="form-success">{msg}</div>}
      {err && <div className="form-error">{err}</div>}

      <div className="approvals-layout">
        {/* List Panel */}
        <div className="approvals-list-panel">
          <h4 style={{ margin: "0 0 1rem 0", fontSize: "0.9rem", color: "var(--text-muted)" }}>Pending Requests ({pendingRequests.length})</h4>
          {loading ? (
            <div>Loading queue…</div>
          ) : pendingRequests.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: "0.8rem", textAlign: "center", padding: "2rem 0" }}>
              No pending approval requests.
            </div>
          ) : (
            pendingRequests.map((req) => (
              <div
                key={req.id}
                className={`approval-item-card ${selectedRequest?.id === req.id ? "approval-item-card--selected" : ""}`}
                onClick={() => setSelectedRequest(req)}
              >
                <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                  {req.requestedByName}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", justifyContent: "space-between" }}>
                  <span>UID: {req.employeeId.slice(0, 8)}…</span>
                  <span>
                    {req.createdAt ? (req.createdAt.toDate ? req.createdAt.toDate().toLocaleDateString() : new Date(req.createdAt).toLocaleDateString()) : ""}
                  </span>
                </div>
                <div style={{ fontSize: "0.7rem", marginTop: "0.5rem", color: "var(--brand-primary, #1b4b31)", fontWeight: 500 }}>
                  ✏️ {Object.keys(req.fields || {}).length} fields modified
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comparison Detail Panel */}
        <div className="form-card" style={{ height: "fit-content" }}>
          {selectedRequest ? (
            <div>
              <h3 style={{ margin: "0 0 0.25rem 0", fontSize: "1.1rem" }}>Review Requested Changes</h3>
              <p className="text-muted" style={{ fontSize: "0.78rem", marginBottom: "1.5rem" }}>
                Submitted by <strong>{selectedRequest.requestedByName}</strong>. Review original values against the proposed updates.
              </p>

              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Field Reference</th>
                    <th>Current Value</th>
                    <th>Proposed Update</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(selectedRequest.fields || {}).map(([key, value]) => {
                    const oldVal = currentValues[key] || "";
                    return (
                      <tr key={key}>
                        <td style={{ fontWeight: 500 }}>{formatFieldName(key)}</td>
                        <td>
                          <span className="value-old">{maskValue(key, oldVal)}</span>
                        </td>
                        <td>
                          <span className="value-new">{maskValue(key, value)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border)", paddingTop: "1.5rem" }}>
                <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                  <label>Rejection / Correction Comments (Required for Rejection)</label>
                  <textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Enter reason for rejecting these modifications..."
                    style={{ minHeight: "80px", marginTop: "0.5rem" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <button className="btn-success" style={{ flex: 1 }} onClick={handleApprove} disabled={processing}>
                    {processing ? "Processing…" : "✓ Approve & Apply"}
                  </button>
                  <button className="btn-danger" style={{ flex: 1 }} onClick={handleReject} disabled={processing}>
                    {processing ? "Processing…" : "✕ Reject Requests"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "260px", color: "var(--text-muted)" }}>
              <span style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📝</span>
              <p style={{ fontSize: "0.85rem" }}>Select an request card from the queue to start reviewing.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
